// AN OPENED DEPOSIT, its custody facts and its KYT side by side (his words, 1 Nov 2026: "Deposits shows KYT details for that
// transaction- essentially combining KYT & Custody here for easier classification and judgement-"; the drawing s4).
//
// The KYT part is the row's own `kyt` object (N-097, the wallet session embeds KYT's answer at intake): `kyt.view` from KYT's
// release (level, finding, sender, Global Ledger score, trace groups, lists read); a deposit rated before it shows the row's
// older kyt* fields in the same places, the missing facts simply absent. KYT's LEVEL (attention) and where the money was
// ROUTED are two facts with two labels — they can differ by design. A share keeps up to two decimals (share(), his rule).
import { useState, type ReactNode } from 'react';
import type { DepositRow } from '../../../lib/api';
import { POOL_WORD, shortAddress } from '../../../lib/custody-format';
import { fmtDay, fmtStamp } from '../../../lib/format';
import { share } from '../../../screens/Kyt';
import { Pill, type PillTone } from '../../Pill';
import { RESULT_WORD } from './deposit-words';
import { Figure } from './parts';

const LEVEL: Record<string, { word: string; tone: PillTone }> = {
  alert: { word: 'Alert', tone: 'bad' },
  watch: { word: 'Watch', tone: 'warn' },
  clean: { word: 'Clean', tone: 'good' },
  unscreened: { word: 'Not screened', tone: 'warn' },
  small: { word: 'Small payment', tone: 'neutral' },
};
/** An older verdict as KYT's own word — never the routing ("clean → Hot" said two facts in one; Routed says where it went). */
const VERDICT: Record<string, string> = { clean: 'Clean', clear: 'Clean', exposed: 'Exposed', review: 'Watch', flagged: 'Alert', pending: 'Not rated yet', error: 'Not rated' };
const GROUP: Record<string, string> = { sanctions: 'Sanctions', criminal: 'Criminal', risky: 'Risky', unknown: 'Unknown', known: 'Known' };

/** Sentence case for a word the read sends in lower case ("clean → Hot" → "Clean → Hot"). */
const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** One fact: the label left and the value right; on the phone the label above the value, so a value never wraps beside it. */
export function Line({ label, children, name = false }: { label: string; children: ReactNode; name?: boolean }) {
  return (
    <div className="grid gap-1 border-b border-stroke-soft py-3 last:border-b-0 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:items-baseline sm:gap-3">
      <div className="text-meta text-tg-hint">{label}</div>
      {/* A name may take two lines (DESIGN-SYSTEM §10.7); every other value one. */}
      <div data-value={name ? 'name' : ''} className="min-w-0 text-body sm:text-right">{children}</div>
    </div>
  );
}

/** A quiet text button that opens what sits behind it (the "why", the list names). */
function Behind({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="tap-44 ml-2 text-meta text-tg-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{label}</button>
      {open && <span className="mt-2 block text-left text-meta text-tg-hint">{children}</span>}
    </>
  );
}

/** THE FINDING: its words clamped to two lines ALONE — Why sits beside them and the explanation opens below, never inside the
 *  clamp (Astra, 2 Nov 2026: a two-line finding hid its own Why). */
function Finding({ text, why }: { text: ReactNode; why: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="block">
      <span className="flex items-baseline justify-between gap-2 sm:justify-end">
        <span data-finding className="line-clamp-2 min-w-0 break-words">{text}</span>
        {why && <button type="button" data-why-button className="tap-44 shrink-0 text-meta text-tg-link" aria-expanded={open} onClick={() => setOpen((x) => !x)}>Why</button>}
      </span>
      {open && why && <span data-why className="mt-2 block text-left text-meta text-tg-hint">{why}</span>}
    </span>
  );
}

/** Copy, beside a shortened address (the whole one is what is copied). */
function Copy({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="tap-44 ml-2 text-meta text-tg-link" onClick={() => { void navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

/** ADDRESS POISONING (KYT's door, 3 Nov 2026 — three look-alikes of a venue's deposit address in a day, desk wallet #58): the
 *  sender reads like an address the desk knows but is not one. KYT's own line, as on its screen (components/v2/kyt/parts.tsx
 *  Finding): red, under the finding, in every layout. */
export const lookalikeOf = (d: DepositRow): string | null => d.kyt?.view?.poisoning?.of ?? null;
export function Lookalike({ d, className = '' }: { d: DepositRow; className?: string }) {
  const of = lookalikeOf(d);
  return of ? <span data-lookalike className={`block whitespace-normal break-words text-meta text-neg ${className}`}>Look-alike — address poisoning · of {of} · never a payee</span> : null;
}

/** Where the money went — the wallet's routing, a separate fact from KYT's level. */
export function routedWord(d: DepositRow): { word: string; tone: string } {
  if (d.status === 'held' || d.kyt?.result === 'pending') return { word: 'Held · not rated yet', tone: 'text-warn' };
  if (d.status === 'rejected') return { word: 'Rejected', tone: 'text-neg' };
  if (d.pool) return { word: POOL_WORD[d.pool] ?? d.pool, tone: '' };
  return { word: 'Not routed yet', tone: 'text-tg-hint' };
}

/** KYT's level as a pill; a deposit without `kyt.view` says its older verdict in words. */
export function LevelWord({ d }: { d: DepositRow }) {
  const level = d.kyt?.view?.level;
  // A look-alike sender's Clean is never green (the CC, 3 Nov 2026): the red line under the finding says why.
  const base = level ? (() => { const l = LEVEL[level] ?? { word: level, tone: 'neutral' as PillTone }; return <Pill tone={d.kyt?.view?.poisoning && l.tone === 'good' ? 'neutral' : l.tone}>{l.word}</Pill>; })()
    : d.kytVerdict ? <span>{VERDICT[d.kytVerdict] ?? cap(RESULT_WORD[d.kytVerdict] ?? d.kytVerdict)}</span>
    : <span className="text-tg-hint">{d.status === 'screening' ? 'Under way' : 'Not rated yet'}</span>;
  // A source named without a figure (N-032): he checks it by hand — said beside the level wherever the level is drawn.
  return d.kytHandCheck ? <span className="inline-flex flex-wrap items-center gap-2">{base}<Pill tone="warn">Check by hand</Pill></span> : base;
}

/** EVERY LIST HIT, ONCE (Astra, 2 Nov 2026): the stored answer's sanctions (listing, else label), the sender listed, the
 *  blacklists, and the older kytLists — one deduplicated list, so the badge's count and Details always say the same hits. */
export function listHits(d: DepositRow): string[] {
  const out = new Set<string>();
  const name = (x: { listing?: string | null; label?: string | null; usd?: number | null }) => (x.listing || x.label || '').trim();
  const add = (n: string) => { if (n) out.add(n); };
  for (const s of d.kyt?.sanctions ?? []) add(name(s));
  const L = d.kytLists;
  if (L?.senderListed) add(`Sender on ${name(L.senderListed) || 'a list'}`);
  for (const b of L?.blacklisted ?? []) add(b.by ? `Blacklisted by ${b.by}` : 'Blacklisted');
  for (const s of L?.sanctions ?? []) add(name(s));
  return [...out];
}

export function DepositKyt({ d }: { d: DepositRow }) {
  const v = d.kyt?.view ?? null;
  const routed = routedWord(d);
  const hits = listHits(d);
  const trace = v?.trace ?? null;
  const groups = (trace?.groups ?? []).filter((g) => g.pct > 0);
  const checkedAt = d.kyt?.checkedAt ?? d.kytRatedAt ?? null;
  const read = v?.lists?.read ?? [];
  const because = d.kytBecause ?? [];
  const of = lookalikeOf(d);
  // The door words its finding "Address poisoning" for a look-alike: then KYT's red line IS the finding, said once.
  const doorSaysIt = !!of && v?.finding?.head === 'Address poisoning';
  return (
    <section>
      <h3 className="mb-1 mt-5 text-label font-medium uppercase tracking-wider text-tg-hint">KYT and route</h3>
      {/* Two facts, two labels (DESIGN-SYSTEM §10.5): KYT's attention, and where the money went. */}
      <div className="grid grid-cols-2 gap-3 border-b border-stroke-soft py-3">
        <div><div className="text-meta text-tg-hint">KYT level</div><div className="mt-1 text-body"><LevelWord d={d} /></div></div>
        <div><div className="text-meta text-tg-hint">Routed</div><div className={`mt-1 text-body ${routed.tone}`}>{routed.word}</div></div>
      </div>
      {/* The finding, attributed to who rated it; left out when it only repeats the level (a plain "Clean"). */}
      {(of || (v?.finding ? !(LEVEL[v.level ?? '']?.word === v.finding.head && !v.finding.source && !v.finding.why) : (d.kyt?.finding || because.length > 0))) && (
        <Line label="Finding" name>
          <Finding
            text={doorSaysIt ? <Lookalike d={d} /> : v?.finding ? (v.finding.source && LEVEL[v.level ?? '']?.word === v.finding.head ? v.finding.source
              : <>{v.finding.head}{v.finding.source ? <span className="text-tg-hint"> · {v.finding.source}</span> : null}</>)
              : d.kyt?.finding?.heading ?? (because[0] ? <>{because[0]}{because.length > 1 && <span className="text-tg-hint"> +{because.length - 1}</span>}</> : null)}
            // Why opens the COMPLETE text whenever there is any (Astra, 2 Nov 2026): KYT's own why, else every reason in full —
            // a single long reason clamped to two lines must still be readable whole.
            why={v?.finding?.why ?? (because.length > 0 ? because.join(' ') : null)}
          />
          {!doorSaysIt && <Lookalike d={d} className="mt-1" />}
        </Line>
      )}
      <Line label="Sender">
        <span>{v?.sender?.name ?? v?.sender?.label ?? 'No name'}</span>
        <span className="text-tg-hint"> · </span>
        {/* A look-alike: the address red with the warning for its title, and no Copy — a copy button is what the attack relies on. */}
        {of
          ? <span className="font-mono text-neg" data-address="lookalike" title={`Address poisoning — look-alike of ${of}. Never a payee; never copy.`}>{shortAddress(d.fromAddress, 6, 4)}</span>
          : <><span className="font-mono">{shortAddress(d.fromAddress, 6, 4)}</span><Copy value={d.fromAddress} /></>}
      </Line>
      {v?.gl && v.gl.score !== null && (
        <Line label="GL score">
          <span className="font-mono tabular">{v.gl.score}</span>{v.gl.band ? <span className={v.gl.score >= 70 ? 'text-neg' : v.gl.score >= 50 ? 'text-warn' : ''}> · {cap(v.gl.band.toLowerCase())}</span> : null}
          <span className="text-tg-hint"> · Global Ledger</span>
        </Line>
      )}
      {hits.length > 0 && (
        <Line label="List hits">
          <Pill tone="bad">{hits[0] ?? ''}</Pill>{hits.length > 1 && <span className="ml-1 text-meta text-tg-hint">+{hits.length - 1}</span>}
          <Behind label="Details"><span className="block space-y-1">{hits.map((l) => <span key={l} className="block">{l}</span>)}</span></Behind>
        </Line>
      )}
      {groups.length > 0 && (
        <table className="mt-3 w-full text-body" aria-label="Exposure by group">
          <thead>
            <tr className="text-meta text-tg-hint">
              <th className="py-2 text-left font-normal">Group</th>
              <th className="py-2 text-right font-normal">Share</th>
              <th className="py-2 text-right font-normal">USD</th>
              <th className="py-2 text-right font-normal">Payments</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.group} className="border-t border-stroke-soft">
                <td className="py-2">{GROUP[g.group] ?? g.group}</td>
                <td className="py-2 text-right font-mono tabular">{share(g.pct)}</td>
                <td className="py-2 text-right">{g.usd === null ? <span className="text-tg-hint">—</span> : <Figure value={String(g.usd)} asset="USD" />}</td>
                <td className="py-2 text-right font-mono tabular">{g.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {trace && (trace.partial || (trace.unread ?? 0) > 0) && (
        <div className="flex items-baseline justify-between border-t border-stroke-soft py-2 text-meta text-tg-hint">
          <span>Not traced</span><span className="font-mono tabular">{trace.unread ? share(trace.unread) : 'in part'}</span>
        </div>
      )}
      {(checkedAt || read.length > 0) && (
        <p className="mt-3 text-meta text-tg-hint">
          {checkedAt && <>Checked <span className="font-mono tabular">{fmtStamp(checkedAt)}</span></>}
          {v?.lists?.refreshedAt && <> · lists of {fmtDay(v.lists.refreshedAt)}</>}
          {read.length > 0 && <Behind label="Lists">{read.join(' · ')}</Behind>}
        </p>
      )}
    </section>
  );
}
